import * as Sentry from '@sentry/react'
import { createContext, createRef, useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'

import { apiKey } from '~/configs/app'
import { ERROR_MESSAGES, SUCCESS_MESSAGES } from '~/constants/messages'
import useAuth from '~/hooks/useAuth'
import useDebugger from '~/hooks/useDebugger'
import { useIndexedDBRecording } from '~/hooks/useIndexedDBRecording'
import useSocket from '~/hooks/useSocket'
import { useVoiceRecorder } from '~/hooks/useVoiceRecorder'
import { api } from '~/services/api'
import type {
  CallContextProps,
  CustomerData,
  LoadingStatesData,
  RoomData,
  SelectedTicketData,
  SocketResGetCustomer,
  SocketResUpdateQueue,
  TicketData,
} from '~/types/call'
import type { CallData, CurrentServingData, SocketReqJoinRoom, SocketResJoinRoom } from '~/types/call'
import type { LayoutProps } from '~/types/layout'
import { Recording } from '~/types/recorder'
import { getAccessToken } from '~/utils/token'

const defaultTicket: TicketData = {
  queue_id: '',
  queue_no: '',
  queue_secret_code: '',
  counter_run_id: '',
  counter_lane: '',
  room: '',
  created_at: '',
  trigger: 'unset',
}

const defaultCustomer: CustomerData = {
  customer_no: '',
  customer_fullname: '',
  customer_email: '',
  customer_keterangan: '',
}

const defaultLoadingStates: LoadingStatesData = {
  start: false,
  stop: false,
  next: false,
  serve: false,
  aux: false,
  skip: false,
  cancel: false,
  recall: false,
  moveBack: false,
  customer: false,
}

const defaultCallData: CallData = {
  antrian_list: [],
  done_list: [],
  idle_list: { A0: defaultTicket },
  proses_list: [],
  skip_list: [],
}

const defaultCounter: RoomData = {
  room: '',
  origin: '',
  counter_id: '',
  counter_lane: '',
  counter_number: '',
}

const defaultIsCallReminderOpen: boolean = false

const CallContext = createContext<CallContextProps>({
  isQueueStarted: false,
  isFirstQueueCalled: false,
  loadingStates: defaultLoadingStates,
  startQueue: () => {},
  stopQueue: () => {},
  counter: defaultCounter,
  callData: defaultCallData,
  selectedTicket: {
    ...defaultTicket,
    isSkip: false,
  },
  currentServing: {
    ticket: defaultTicket,
    customer: defaultCustomer,
    isIdle: false,
    isServing: false,
  },
  selectQueue: () => {},
  nextQueue: () => {},
  serveQueue: () => {},
  auxQueue: () => {},
  skipQueue: () => {},
  cancelQueue: () => {},
  recallQueue: () => {},
  moveBackQueue: () => {},
  getCustomer: () => {},
  updateCustomer: () => {},
  resetCustomer: () => {},
  formRef: createRef<HTMLFormElement>(),
  recordings: [],
  audioPermissionStatus: 'prompt' as 'granted' | 'denied' | 'prompt',
  isCallReminderOpen: defaultIsCallReminderOpen,
  setIsCallReminderOpen: () => {},
})

const CallProvider = ({ children }: LayoutProps) => {
  const socket = useSocket()
  const { profile } = useAuth()
  const token = getAccessToken()
  const { debug } = useDebugger()

  const formRef = useRef<HTMLFormElement>(null)
  const [isQueueStarted, setIsQueueStarted] = useState<boolean>(false)
  const [isFirstQueueCalled, setIsFirstQueueCalled] = useState<boolean>(false)
  const [loadingStates, setLoadingStates] = useState(defaultLoadingStates)
  const [counter, setCounter] = useState<RoomData>(defaultCounter)
  const [callData, setCallData] = useState<CallData>(defaultCallData)
  const [selectedTicket, setSelectedTicket] = useState<SelectedTicketData>({ ...defaultTicket, isSkip: false })
  const [currentServing, setCurrentServing] = useState<CurrentServingData>({
    ticket: defaultTicket,
    customer: defaultCustomer,
    isIdle: false,
    isServing: false,
  })
  const [isCallReminderOpen, setIsCallReminderOpen] = useState<boolean>(defaultIsCallReminderOpen)
  const shouldSaveRecordingRef = useRef<boolean>(true)

  const [recordings, setRecordings] = useState<Recording[]>([])
  const dbRef = useRef<IDBDatabase | null>(null)

  const {
    initDb,
    storeRecording,
    getStoredRecordings,
    setRecordingsUploaded,
    removeUploadedRecording,
    removeZeroSizeRecordings,
  } = useIndexedDBRecording()

  const handleRecordingComplete = async (recording: Omit<Recording, 'id'>) => {
    if (!dbRef.current) return

    if (!shouldSaveRecordingRef.current) return

    try {
      const recordingId = await storeRecording(dbRef.current, recording)

      await updateRecordings()

      await uploadRecordingToServer({
        ...recording,
        id: recordingId,
      } as Recording)
    } catch (error) {
      console.error('Error handling recording:', error)
    }
  }

  const { audioPermissionStatus, startRecording, stopRecording, transcodeAudioToMp4 } =
    useVoiceRecorder(handleRecordingComplete)

  const updateRecordings = async () => {
    if (!dbRef.current) return
    const storedRecordings = await getStoredRecordings(dbRef.current)
    setRecordings(storedRecordings)
  }

  const uploadRecordingsInParallel = async (recordings: Recording[], maxConcurrent = 2) => {
    if (!dbRef.current) return

    // Process recordings in chunks
    for (let i = 0; i < recordings.length; i += maxConcurrent) {
      const chunk = recordings.slice(i, i + maxConcurrent)
      // Upload the chunk in parallel
      const uploadPromises = chunk.map(async (recording, index) => {
        try {
          console.log(`Uploading recording ${recording.id} (${i + index + 1}/${recordings.length})`)
          await uploadRecordingToServer(recording)
          console.log(`Successfully uploaded recording ${recording.id}`)
        } catch (error) {
          console.error(`Failed to upload recording ${recording.id}:`, error)
        }
      })

      // Wait for all uploads in this chunk to complete before moving to the next chunk
      await Promise.all(uploadPromises)
    }
  }

  const uploadRecordingToServer = async (recording: Recording, retryWithOriginal = false) => {
    if (!dbRef.current) return

    try {
      let audioFile: Blob
      if (retryWithOriginal) {
        // Use original blob if retrying or already mp4
        audioFile = recording.blob
      } else {
        const transcodedAudio = await transcodeAudioToMp4(recording.blob)
        audioFile = transcodedAudio
      }

      const formData = new FormData()
      formData.append('counter_run_id', recording.counterRunId)
      formData.append('audio', audioFile)

      await api.post('/upload-voice-record', formData)

      await setRecordingsUploaded(dbRef.current, recording.id)
      await updateRecordings()
    } catch (error) {
      console.error('Error uploading recording:', error)

      // Handle FS (File System) errors from FFmpeg
      const errorMessage = error instanceof Error ? error.message : String(error)

      if (!retryWithOriginal && errorMessage.includes('FS error')) {
        try {
          // Retry with original blob without transcoding
          await uploadRecordingToServer(recording, true)
          return
        } catch (retryError) {
          console.error('Error retrying upload with original blob:', retryError)
          Sentry.captureException(retryError)
        }
      }

      Sentry.captureException(error)
    }
  }

  const isInitialized = useRef(false)
  useEffect(() => {
    if (isInitialized.current) return
    isInitialized.current = true

    const initialize = async () => {
      try {
        const database = await initDb()
        dbRef.current = database

        // Remove zero-size blob records first
        await removeZeroSizeRecordings(database)

        const storedRecordings = await getStoredRecordings(database)
        const isNotUploadedRecordings = storedRecordings.filter(
          (recording) => !recording.isUploaded && recording.blob.size > 0,
        )
        setRecordings(isNotUploadedRecordings)

        // Upload recordings in concurrent in case user offline or upload isn't finished
        await uploadRecordingsInParallel(isNotUploadedRecordings, 1)

        await removeUploadedRecording(database)
      } catch (error) {
        console.error('Error during initialization:', error)
      }
    }

    initialize().catch((error) => {
      console.error('Error during initialization:', error)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const headers = {
    headers: {
      Authorization: `Bearer ${token}`,
      'X-API-KEY': apiKey,
    },
  }
  const user_id = profile?.id
  const username = profile?.username

  const startQueue = (reqData: SocketReqJoinRoom) => {
    setLoadingStates((prevState) => ({ ...prevState, start: true }))

    const { location_id, counter_id, counter_type, counter_number } = reqData
    const room = location_id.toString() + counter_type.toString()
    const origin = location_id.toString() + counter_type.toString() + counter_number.toString()
    const counter_lane = counter_type.toString() + counter_number.toString()

    if (socket) {
      socket.emit(
        'join',
        {
          room,
          origin,
          user_type: 'cs',
          uid: profile?.id,
          headers,
          payload: {
            counter_id,
            location_id,
          },
        },
        (response: SocketResJoinRoom) => {
          if (response.status) {
            const { antrian_list, done_list, idle_list, proses_list, skip_list } = response.info

            setCounter({
              room,
              origin,
              counter_id,
              counter_lane,
              counter_number,
            })

            setCallData({
              antrian_list,
              done_list,
              idle_list,
              proses_list,
              skip_list,
            })
            if (counter_lane in idle_list) {
              const { current_status, details, queue_no, queue_secret_code, counter_run_id } = idle_list[counter_lane]

              if (current_status === 99 && details) {
                setCurrentServing({
                  ticket: idle_list[counter_lane],
                  customer: {
                    customer_no: details.customer_no || '',
                    customer_fullname: details.customer_name || '',
                    customer_email: details.customer_email || '',
                    customer_keterangan: details.customer_keterangan || '',
                  },
                  isIdle: true,
                  isServing: true,
                })

                startRecording({
                  queueNo: queue_no,
                  counterRunId: counter_run_id as string,
                  queueSecretCode: queue_secret_code,
                }).catch((error) => {
                  console.log('error on start recording', error)
                  Sentry.captureException(error)
                })
              } else {
                setCurrentServing({
                  ...currentServing,

                  ticket: idle_list[counter_lane],
                  isIdle: true,
                })
              }
            }
            toast.success(SUCCESS_MESSAGES.SUCCESS_START_QUEUE)
            setIsQueueStarted(true)
          } else {
            toast.error(response.msg)
          }
          setLoadingStates((prevState) => ({ ...prevState, start: false }))

          debug('info', 'Socket `join` response:', response)
        },
      )
    }
  }

  const stopQueue = () => {
    setLoadingStates((prevState) => ({ ...prevState, stop: true }))

    if (socket) {
      socket.close()
      socket.connect()
      setIsQueueStarted(false)
      setIsFirstQueueCalled(false)
      setSelectedTicket({
        ...defaultTicket,
        isSkip: false,
      })
      setCallData(defaultCallData)
      setCurrentServing({
        ticket: defaultTicket,
        customer: defaultCustomer,
        isIdle: false,
        isServing: false,
      })
      setLoadingStates((prevState) => ({ ...prevState, stop: false }))
      toast.error(SUCCESS_MESSAGES.SUCCESS_STOP_QUEUE)
    }
  }

  const selectQueue = (queueId: string, list: 'queue' | 'skip') => {
    let ticketData
    if (list === 'queue') {
      ticketData = callData.antrian_list.find((item) => item.queue_id === queueId)
    } else if (list === 'skip') {
      ticketData = callData.skip_list.find((item) => item.queue_id === queueId)
    }

    if (ticketData) {
      const { queue_id, queue_no, queue_secret_code, counter_run_id, counter_lane, room, created_at, queue_necessary } =
        ticketData

      setSelectedTicket({
        queue_id,
        queue_no,
        queue_secret_code,
        counter_run_id,
        counter_lane,
        room,
        created_at,
        queue_necessary,
        isSkip: list === 'skip',
      })
    }
  }

  const nextQueue = () => {
    setLoadingStates((prevState) => ({ ...prevState, next: true }))

    const trigger = 'next'
    const nextTicket = selectedTicket.queue_id ? selectedTicket : callData.antrian_list[0]
    const { queue_id } = nextTicket
    const { room, origin, counter_id, counter_lane } = counter

    if (socket) {
      socket.emit(
        'cs_action',
        {
          room,
          origin,
          headers,
          payload: {
            trigger,
            counter_id,
            counter_lane,
            user_id,
            username,
            queue_id,
          },
        },
        (response: SocketResUpdateQueue) => {
          const { status, msg } = response

          if (status) {
            if (!isFirstQueueCalled) setIsFirstQueueCalled(true)
            toast.success(SUCCESS_MESSAGES.SUCCESS_NEXT_QUEUE)
          } else {
            toast.error(msg)
          }
          debug('info', 'Socket `cs_action - next` response:', response)

          setLoadingStates((prevState) => ({ ...prevState, next: false }))
        },
      )
    }
  }

  const serveQueue = () => {
    setLoadingStates((prevState) => ({ ...prevState, serve: true }))

    const trigger = 'serve'
    const { room, origin, counter_id, counter_lane } = counter
    const { queue_id, counter_run_id } = currentServing.ticket
    const {
      customer_no,
      customer_fullname: customer_name,
      customer_email,
      customer_keterangan,
    } = currentServing.customer
    const payload = {
      trigger,
      counter_run_id,
      counter_id,
      counter_lane,
      user_id,
      username,
      queue_id,
      customer_no,
      customer_name,
      customer_email,
      customer_keterangan,
    }

    if (socket) {
      socket.emit(
        'cs_action',
        {
          room,
          origin,
          headers,
          payload,
        },
        (response: SocketResUpdateQueue) => {
          const { status, msg } = response

          if (status) {
            toast.success(SUCCESS_MESSAGES.SUCCESS_SERVE_QUEUE)

            startRecording({
              queueNo: response.info.queue_no,
              queueSecretCode: response.info.queue_secret_code,
              counterRunId: response.info.counter_run_id as string,
            }).catch((error) => {
              console.log('error on start recording', error)
            })
          } else {
            toast.error(msg)
          }
          debug('info', 'Socket `cs_action - serve` response:', response)

          setLoadingStates((prevState) => ({ ...prevState, serve: false }))
        },
      )
    }
  }

  const auxQueue = () => {
    setLoadingStates((prevState) => ({ ...prevState, aux: true }))

    const trigger = 'finish'
    const { room, origin, counter_id, counter_lane } = counter
    const { queue_id, counter_run_id } = currentServing.ticket
    const {
      customer_no,
      customer_fullname: customer_name,
      customer_email,
      customer_keterangan,
    } = currentServing.customer
    const payload = {
      trigger,
      counter_run_id,
      counter_id,
      counter_lane,
      user_id,
      username,
      queue_id,
      customer_no,
      customer_name,
      customer_email,
      customer_keterangan,
    }

    if (socket) {
      socket.emit(
        'cs_action',
        {
          room,
          origin,
          headers,
          payload,
        },
        (response: SocketResUpdateQueue) => {
          const { status, msg } = response

          if (status) {
            shouldSaveRecordingRef.current = true
            stopRecording()
            toast.success(SUCCESS_MESSAGES.SUCCESS_AUX_QUEUE)
          } else {
            toast.error(msg)
          }
          debug('info', 'Socket `cs_action - finish` response:', response)

          setLoadingStates((prevState) => ({ ...prevState, aux: false }))
        },
      )
    }
  }

  const skipQueue = () => {
    setLoadingStates((prevState) => ({ ...prevState, skip: true }))

    const trigger = 'skip'
    const { room, origin, counter_id, counter_lane } = counter
    const { queue_id, counter_run_id } = currentServing.ticket
    const payload = {
      trigger,
      counter_run_id,
      counter_id,
      counter_lane,
      user_id,
      username,
      queue_id,
    }
    debug('info', 'Socket `cs_action - skip` payload:', payload)

    if (socket) {
      socket.emit(
        'cs_action',
        {
          room,
          origin,
          headers,
          payload,
        },
        (response: SocketResUpdateQueue) => {
          const { status, msg } = response

          if (status) {
            shouldSaveRecordingRef.current = false
            stopRecording()

            toast.error(SUCCESS_MESSAGES.SUCCESS_SKIP_QUEUE)
          } else {
            toast.error(msg)
          }
          debug('info', 'Socket `cs_action - skip` response:', response)

          setLoadingStates((prevState) => ({ ...prevState, skip: false }))
        },
      )
    }
  }

  const cancelQueue = (reason: string, onSuccess?: () => void) => {
    setLoadingStates((prevState) => ({ ...prevState, cancel: true }))

    const trigger = 'cancel'
    const { room, origin, counter_id, counter_lane } = counter
    const { queue_id, counter_run_id } = currentServing.ticket
    const payload = {
      trigger,
      counter_run_id,
      counter_id,
      counter_lane,
      user_id,
      username,
      queue_id,
      cancel_reason: reason,
    }
    debug('info', 'Socket `cs_action - cancel` payload:', payload)

    if (socket) {
      socket.emit(
        'cs_action',
        {
          room,
          origin,
          headers,
          payload,
        },
        (response: SocketResUpdateQueue) => {
          const { status, msg } = response

          if (status) {
            // rekaman disimpan sebagai bukti alasan pembatalan
            shouldSaveRecordingRef.current = true
            stopRecording()
            onSuccess?.()

            toast.success(SUCCESS_MESSAGES.SUCCESS_CANCEL_QUEUE)
          } else {
            toast.error(msg)
          }
          debug('info', 'Socket `cs_action - cancel` response:', response)

          setLoadingStates((prevState) => ({ ...prevState, cancel: false }))
        },
      )
    }
  }

  const recallQueue = () => {
    setLoadingStates((prevState) => ({ ...prevState, recall: true }))

    const trigger = 'recall'
    const { room, origin, counter_id, counter_lane } = counter
    const { queue_id, counter_run_id } = currentServing.ticket
    const payload = {
      trigger,
      counter_run_id,
      counter_id,
      counter_lane,
      user_id,
      username,
      queue_id,
    }

    if (socket) {
      socket.emit(
        'cs_action',
        {
          room,
          origin,
          headers,
          payload,
        },
        (response: SocketResUpdateQueue) => {
          const { status, msg } = response

          if (status) {
            toast.success(SUCCESS_MESSAGES.SUCCESS_RECALL_QUEUE)
          } else {
            toast.error(msg)
          }
          debug('info', 'Socket `cs_action - recall` response:', response)

          setLoadingStates((prevState) => ({ ...prevState, recall: false }))
        },
      )
    }
  }

  const moveBackQueue = () => {
    setLoadingStates((prevState) => ({ ...prevState, moveBack: true }))

    const trigger = 'moveback'
    const { room, origin, counter_id, counter_lane } = counter
    const { queue_id, counter_run_id } = selectedTicket
    const payload = {
      trigger,
      counter_run_id,
      counter_id,
      counter_lane,
      user_id,
      username,
      queue_id,
    }

    if (socket) {
      socket.emit(
        'cs_action',
        {
          room,
          origin,
          headers,
          payload,
        },
        (response: SocketResUpdateQueue) => {
          const { status, msg } = response

          if (status) {
            toast.success(SUCCESS_MESSAGES.SUCCESS_MOVE_BACK_QUEUE)
          } else {
            toast.error(msg)
          }
          debug('info', 'Socket `cs_action - moveback` response:', response)

          setLoadingStates((prevState) => ({ ...prevState, moveBack: false }))
        },
      )
    }
  }

  const getCustomer = (customer_no: string) => {
    setLoadingStates((prevState) => ({ ...prevState, customer: true }))

    const { room } = counter

    if (socket) {
      socket.emit(
        'get_customer_no',
        {
          room,
          headers,
          payload: {
            customer_no,
          },
        },
        (response: SocketResGetCustomer) => {
          const { status, info } = response

          if (status) {
            const { customer_no, customer_fullname, customer_email } = info

            setCurrentServing({
              ...currentServing,
              customer: {
                customer_no,
                customer_fullname,
                customer_email,
              },
            })
          } else {
            toast.error(ERROR_MESSAGES.CUSTOMER_NOT_FOUND)
          }
          debug('info', 'Socket `get_customer_no` response:', response)
          setLoadingStates((prevState) => ({ ...prevState, customer: false }))
        },
      )
    }
  }

  const updateCustomer = (customer_data: CustomerData) => {
    const { customer_no, customer_fullname, customer_email, customer_keterangan } = customer_data

    setCurrentServing({
      ...currentServing,
      customer: {
        customer_no,
        customer_fullname,
        customer_email,
        customer_keterangan,
      },
    })
  }

  const resetCustomer = () => {
    setCurrentServing({
      ...currentServing,
      customer: defaultCustomer,
    })
  }

  const handleQueueUpdate = useCallback(
    (response: SocketResUpdateQueue) => {
      setCallData((prevCallData) => {
        if (prevCallData.antrian_list.some((item) => item.queue_id === response.info.queue_id)) {
          return prevCallData
        }
        return {
          ...prevCallData,
          antrian_list: [...prevCallData.antrian_list, response.info],
        }
      })
      debug('info', 'Socket `queue_update` response:', response)
    },
    [debug],
  )

  const handleCsUpdate = useCallback(
    (response: SocketResUpdateQueue) => {
      const ticket = response.info

      setCallData((prevCallData) => {
        let updatedQueueList = [...prevCallData.antrian_list]
        let updatedSkipList = [...prevCallData.skip_list]
        const updatedDoneList = [...prevCallData.done_list]

        if (ticket.trigger === 'next') {
          if (ticket.counter_lane === counter.counter_lane) {
            setCurrentServing({
              ...currentServing,
              ticket,
              isIdle: true,
            })
            setSelectedTicket({
              ...defaultTicket,
              isSkip: false,
            })
          }
          updatedQueueList = updatedQueueList.filter((item) => item.queue_id !== ticket.queue_id)
        }

        if (ticket.trigger === 'serve') {
          if (ticket.counter_lane === counter.counter_lane) {
            setCurrentServing({
              ...currentServing,
              isServing: true,
            })
          }
        }

        if (ticket.trigger === 'finish') {
          if (ticket.counter_lane === counter.counter_lane) {
            setCurrentServing({
              ticket: defaultTicket,
              customer: defaultCustomer,
              isIdle: false,
              isServing: false,
            })
          }
          if (!updatedDoneList.some((item) => item.queue_id === ticket.queue_id)) {
            updatedDoneList.push(ticket)
          }
        }

        if (ticket.trigger === 'skip') {
          if (ticket.counter_lane === counter.counter_lane) {
            setCurrentServing({
              ticket: defaultTicket,
              customer: defaultCustomer,
              isIdle: false,
              isServing: false,
            })
          }
          if (!updatedSkipList.some((item) => item.queue_id === ticket.queue_id)) {
            updatedSkipList.push(ticket)
          }
        }

        if (ticket.trigger === 'cancel') {
          if (ticket.counter_lane === counter.counter_lane) {
            setCurrentServing({
              ticket: defaultTicket,
              customer: defaultCustomer,
              isIdle: false,
              isServing: false,
            })
          }
          updatedQueueList = updatedQueueList.filter((item) => item.queue_id !== ticket.queue_id)
          updatedSkipList = updatedSkipList.filter((item) => item.queue_id !== ticket.queue_id)
        }

        if (ticket.trigger === 'moveback') {
          setSelectedTicket({
            ...defaultTicket,
            isSkip: false,
          })
          if (ticket.counter_lane === counter.counter_lane) {
            setCurrentServing({
              ...currentServing,
              ticket,
              isIdle: true,
            })
          }
          updatedSkipList = updatedSkipList.filter((item) => item.queue_id !== ticket.queue_id)
        }

        return {
          ...prevCallData,
          antrian_list: updatedQueueList,
          skip_list: updatedSkipList,
          done_list: updatedDoneList,
        }
      })

      debug('info', 'Socket `cs_update` response:', response)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentServing, counter],
  )

  useEffect(() => {
    if (socket) {
      socket.on('queue_update', handleQueueUpdate)
      socket.on('cs_update', handleCsUpdate)

      return () => {
        socket.off('queue_update', handleQueueUpdate)
        socket.off('cs_update', handleCsUpdate)
      }
    }
  }, [socket, handleQueueUpdate, handleCsUpdate])

  return (
    <CallContext.Provider
      value={{
        isQueueStarted,
        isFirstQueueCalled,
        loadingStates,
        startQueue,
        stopQueue,
        counter,
        callData,
        selectedTicket,
        currentServing,
        selectQueue,
        nextQueue,
        serveQueue,
        auxQueue,
        skipQueue,
        cancelQueue,
        recallQueue,
        moveBackQueue,
        getCustomer,
        updateCustomer,
        resetCustomer,
        formRef,
        recordings,
        audioPermissionStatus,
        isCallReminderOpen,
        setIsCallReminderOpen,
      }}
    >
      {children}
    </CallContext.Provider>
  )
}

export { CallContext, CallProvider }
